import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { keyframes } from '@mui/system';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import GlobalStyles from '@mui/material/GlobalStyles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { ApiFetch } from '../App';
import { PerformancePlot } from './PerformancePlot';

// ─── Types ────────────────────────────────────────────────────────────────────

type NormaColor = 'rosso' | 'giallo' | 'verde' | 'n/d';

interface RelationalSystemNormeValori {
  Conoscersi: string;
  Discutere: string;
  Connettersi: string;
}

interface RelationalDims {
  Activation: number | 'n/d';
  Reciprocity: number | 'n/d';
  OrganizationalAwareness: number | 'n/d';
  CollectiveSensemaking: number | 'n/d';
  OrganizationalRegulation: number | 'n/d';
  LearningAndPlasticity: number | 'n/d';
}

interface AreaBlock {
  mean: number;
  color: NormaColor;
}

interface AreaClassificationResult {
  Fondamentali: AreaBlock;
  NormeIndividuo: AreaBlock;
  NormeGruppo: AreaBlock;
  NormeOrganizzazione: AreaBlock;
  CapitaleSociale: AreaBlock;
}

interface AreaRecommendation {
  area: string;
  label: string;
  priorityScore: number;
  estimatedImpact: number;
  roomForImprovement: number;
  description: string;
}

interface OutcomeRecommendations {
  outcome: string;
  modelFit: number;
  segment: string;
  rankedAreas: AreaRecommendation[];
}

interface PerformanceRecommendations {
  Crescita: OutcomeRecommendations;
  Risultati: OutcomeRecommendations;
  Sostenibilita: OutcomeRecommendations;
  Velocita: OutcomeRecommendations;
  PerformancePercepita: OutcomeRecommendations;
}

interface RelationalSystemResult {
  probabilita_performance: NormaColor;
  performance_percepita: string;
  warnings: string[];
  norme: Record<string, string>;
  normeValori: RelationalSystemNormeValori;
  voce_bedrock: string;
  relational: RelationalDims;
  ppt: {
    perceptionPerf: NormaColor;
    probabilityPerf: NormaColor;
    normsColors: NormaColor[];
    warnings: string[];
    voce_bedrock: string;
  };
  areaClassifications?: AreaClassificationResult;
  performanceRecommendations?: PerformanceRecommendations;
}

interface RelationalReportSnapshot {
  teamName: string;
  isRelational: boolean;
  analyzedAt: string;
  result: RelationalSystemResult;
}

// ─── Animations ───────────────────────────────────────────────────────────────

const cellBrighten = keyframes`
  from { filter: brightness(0.35); }
  to   { filter: brightness(1); }
`;

// ─── Constants ────────────────────────────────────────────────────────────────

const WARNING_KEY_MAP: Record<string, string> = {
  Frustrazione: 'analysis.warning_frustrazione',
  'Capacità di problem solving': 'analysis.warning_problem_solving',
  Stanchezza: 'analysis.warning_stanchezza',
  'Eccessiva zona di comfort': 'analysis.warning_comfort',
  'Orientamento tattico': 'analysis.warning_tattico',
  'Difficoltà nella messa a terra': 'analysis.warning_messa_a_terra',
  Frenesia: 'analysis.warning_frenesia',
  'Eccessiva tranquillità': 'analysis.warning_tranquillita',
  'Scarso apprendimento': 'analysis.warning_apprendimento',
  'Scarsa capacità realizzativa': 'analysis.warning_realizzativa',
  'Rischi di turnover': 'analysis.warning_turnover',
  'Scarsa spinta': 'analysis.warning_spinta',
};

const RELATIONAL_KEY_MAP: Record<string, string> = {
  Activation: 'analysis.relational_activation',
  Reciprocity: 'analysis.relational_reciprocity',
  OrganizationalAwareness: 'analysis.relational_awareness',
  CollectiveSensemaking: 'analysis.relational_sensemaking',
  OrganizationalRegulation: 'analysis.relational_regulation',
  LearningAndPlasticity: 'analysis.relational_plasticity',
};

const COLOR_HEX: Record<NormaColor, string> = {
  verde: '#2e7d32',
  giallo: '#e65100',
  rosso: '#c62828',
  'n/d': '#616161',
};

// normsColors order from backend: [Conoscersi, Discutere, Connettersi]
const NORM_META = [
  { key: 'Conoscersi' as keyof RelationalSystemNormeValori, labelKey: 'report.norm_conoscersi' },
  { key: 'Discutere' as keyof RelationalSystemNormeValori, labelKey: 'report.norm_discutere' },
  { key: 'Connettersi' as keyof RelationalSystemNormeValori, labelKey: 'report.norm_connettersi' },
];

// One norm per column
const NORM_COLUMNS = [
  { headingKey: 'report.col_individuo', index: 0 },
  { headingKey: 'report.col_gruppo', index: 1 },
  { headingKey: 'report.col_organizzazione', index: 2 },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function NormsHeatmap({ normsColors }: { normsColors: NormaColor[] }) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = useState(false);
  const wasFullyVisible = useRef<boolean | null>(null);
  const hasLeftScreen = useRef(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const fullVisibilityObserver = new IntersectionObserver(
      ([entry]) => {
        const entering = entry.isIntersecting && wasFullyVisible.current === false;
        wasFullyVisible.current = entry.isIntersecting;
        if (entering && hasLeftScreen.current) {
          hasLeftScreen.current = false;
          setAnimating(false);
          requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
        }
      },
      { threshold: 1.0, rootMargin: '0px 0px -20px 0px' }
    );

    const exitObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          hasLeftScreen.current = true;
          setAnimating(false);
        }
      },
      { threshold: 0 }
    );

    fullVisibilityObserver.observe(el);
    exitObserver.observe(el);
    return () => {
      fullVisibilityObserver.disconnect();
      exitObserver.disconnect();
    };
  }, []);

  return (
    <Grid ref={containerRef} container spacing={2} sx={{ mb: 4 }}>
      {NORM_COLUMNS.map(({ headingKey, index }, ci) => {
        const { key, labelKey } = NORM_META[index];
        const color = normsColors[index] ?? 'n/d';
        return (
          <Grid key={key} size={{ xs: 12, sm: 4 }}>
            <Typography
              variant="overline"
              fontWeight={700}
              color="text.secondary"
              align="center"
              display="block"
              sx={{ mb: 1 }}
            >
              {t(headingKey)}
            </Typography>
            <Box
              className="norm-cell"
              sx={{
                bgcolor: COLOR_HEX[color],
                borderRadius: 1.5,
                minHeight: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 2,
                filter: animating ? undefined : 'brightness(0.35)',
                animation: animating ? `${cellBrighten} 1.2s ease-out ${ci * 0.6}s both` : 'none',
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                {t(labelKey)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

function RelationalDimsSection({ relational }: { relational: RelationalDims }) {
  const { t } = useTranslation();
  const dims = (Object.entries(relational) as [string, number | 'n/d'][]).filter(
    ([k]) => k !== '_raw_1to5'
  );

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {dims.map(([key, value]) => (
        <Grid key={key} size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="overline" color="text.secondary" display="block">
                {RELATIONAL_KEY_MAP[key] ? t(RELATIONAL_KEY_MAP[key]) : key}
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {typeof value === 'number' ? value.toFixed(1) : value}
              </Typography>
              {typeof value === 'number' && (
                <Typography variant="caption" color="text.secondary">
                  / 100
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function AreaClassificationsSection({
  areaClassifications,
}: {
  areaClassifications: AreaClassificationResult;
}) {
  const { t } = useTranslation();

  const AREA_META: Array<{ key: keyof AreaClassificationResult; labelKey: string }> = [
    { key: 'Fondamentali', labelKey: 'analysis.medie_fondamentali' },
    { key: 'NormeIndividuo', labelKey: 'report.area_norme_individuo' },
    { key: 'NormeGruppo', labelKey: 'report.area_norme_gruppo' },
    { key: 'NormeOrganizzazione', labelKey: 'report.area_norme_organizzazione' },
    { key: 'CapitaleSociale', labelKey: 'analysis.medie_capitale_sociale' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {AREA_META.map(({ key, labelKey }) => {
        const { mean, color } = areaClassifications[key];
        return (
          <Grid key={key} size={{ xs: 6, sm: 4 }}>
            <Box
              className="norm-cell"
              sx={{
                bgcolor: COLOR_HEX[color],
                borderRadius: 1.5,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 80,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {t(labelKey)}
              </Typography>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                {mean.toFixed(2)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

function RecommendationsSection({
  recommendations,
}: {
  recommendations: PerformanceRecommendations;
}) {
  const { t } = useTranslation();

  const AREA_LABEL_MAP: Record<string, string> = {
    Fondamentali: t('analysis.medie_fondamentali'),
    NormeIndividuo: t('report.area_norme_individuo'),
    NormeGruppo: t('report.area_norme_gruppo'),
    NormeOrganizzazione: t('report.area_norme_organizzazione'),
    CapitaleSociale: t('analysis.medie_capitale_sociale'),
  };

  const OUTCOME_META: Array<{ key: keyof PerformanceRecommendations; labelKey: string }> = [
    { key: 'Crescita', labelKey: 'analysis.medie_crescita' },
    { key: 'Risultati', labelKey: 'analysis.medie_risultati' },
    { key: 'Sostenibilita', labelKey: 'analysis.medie_sostenibilita' },
    { key: 'Velocita', labelKey: 'analysis.medie_velocita' },
    { key: 'PerformancePercepita', labelKey: 'analysis.performance_percepita' },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {OUTCOME_META.map(({ key, labelKey }) => {
        const rec = recommendations[key];
        return (
          <Grid key={key} size={{ xs: 12, sm: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {t(labelKey)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {t('report.recommendations_model_fit', { value: rec.modelFit.toFixed(2) })}
                  {' · '}
                  {t(
                    rec.segment === 'low'
                      ? 'report.recommendations_segment_low'
                      : 'report.recommendations_segment_high'
                  )}
                </Typography>
                {rec.rankedAreas.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('report.recommendations_no_areas')}
                  </Typography>
                ) : (
                  rec.rankedAreas.map((area, i) => (
                    <Box
                      key={area.area}
                      sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: 16, flexShrink: 0 }}
                      >
                        {i + 1}.
                      </Typography>
                      <Typography variant="body2" fontWeight={i === 0 ? 600 : 400}>
                        {AREA_LABEL_MAP[area.area] ?? area.label}
                      </Typography>
                    </Box>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface RelationalReportProps {
  surveyId: string;
  apiFetch: ApiFetch;
  mode: 'dark' | 'light';
  toggleMode: () => void;
}

export const RelationalReport: React.FC<RelationalReportProps> = ({
  surveyId,
  apiFetch,
  mode,
  toggleMode,
}) => {
  const { t, i18n } = useTranslation();
  const { palette } = useTheme();
  const primary = palette.primary.main;
  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  };
  const [snapshot, setSnapshot] = useState<RelationalReportSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await apiFetch({ operation: 'get_report', surveyId, lang: i18n.language });
        if (!response.ok) {
          setError(t('report.load_error'));
          return;
        }
        const json = await response.json();
        if (json['output']?.error) {
          setError(json['output'].error);
        } else {
          setSnapshot(json['output'] as RelationalReportSnapshot);
        }
      } catch {
        setError(t('report.network_error'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !snapshot) {
    return (
      <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error">{error ?? t('report.not_found')}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => (window.location.href = '/')}>
          {t('report.back_to_app')}
        </Button>
      </Box>
    );
  }

  const normaColorToLevel = (c: NormaColor): 1 | 2 | 3 =>
    c === 'verde' ? 3 : c === 'giallo' ? 2 : 1;

  const { teamName, analyzedAt, result } = snapshot;
  const { ppt, voce_bedrock, relational, areaClassifications, performanceRecommendations } = result;
  const themes = voce_bedrock
    .replaceAll('THEME:', 'TEMA:')
    .replaceAll('|', ', ')
    .split('TEMA:')
    .map((s) => s.trim())
    .filter((s) => s !== '');

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'html, body': { backgroundColor: 'white !important', color: 'black !important' } as any,
            '.no-print': { display: 'none !important' },
            '.print-only': { display: 'block !important' },
            '.temi-section': { pageBreakBefore: 'always', breakBefore: 'page' },
            '.MuiTypography-root': { color: 'black !important' },
            '.norm-cell': {
              WebkitPrintColorAdjust: 'exact !important',
              printColorAdjust: 'exact !important',
              filter: 'brightness(1) !important',
              animation: 'none !important',
            } as any,
            '.norm-cell .MuiTypography-root': { color: 'white !important' },
            '.MuiCard-root, .MuiPaper-root': { backgroundColor: 'white !important' },
            '.MuiDivider-root': { borderColor: '#aaa !important' },
            '.MuiLinearProgress-root': { backgroundColor: '#e0e0e0 !important' },
            '.MuiChip-root': { borderColor: '#aaa !important', color: 'black !important' },
          },
        }}
      />

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 4 } }}>
        {/* ── Header ── */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.default',
            borderBottom: 1,
            borderColor: 'divider',
            py: 2,
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
          className="no-print"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              TeamEI
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Typography variant="body1" fontWeight={500}>
              {teamName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {t('report.analyzed_at', { date: analyzedAt })}
            </Typography>
            <Select
              size="small"
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              variant="standard"
              disableUnderline
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="it">IT</MenuItem>
              <MenuItem value="en">EN</MenuItem>
            </Select>
            <IconButton onClick={toggleMode} color="inherit" size="small">
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <Button size="small" variant="outlined" onClick={() => window.print()}>
              {t('report.print')}
            </Button>
            <Button size="small" onClick={() => (window.location.href = '/')}>
              {t('report.back_to_app')}
            </Button>
          </Box>
        </Box>

        {/* ── Title (print only) ── */}
        <Box sx={{ mb: 4, display: 'none' }} className="print-only">
          <Typography variant="h5" fontWeight={700}>
            {t('report.print_title', { teamName })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('report.analyzed_at', { date: analyzedAt })} · Relational System
          </Typography>
        </Box>

        {/* ── About TEAMEI ── */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('report.about_title')}
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" fontStyle="italic" color="text.secondary" sx={{ mb: 2 }}>
            {t('report.about_relational_quote')}
            <br />
            <em>{t('report.about_relational_quote_author')}</em>
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
            {t('report.about_relational_p1')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('report.about_relational_p2')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('report.about_relational_p3')}
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {t('report.about_relational_tagline')}
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />
      </Box>

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 4 }, pb: 8 }}>
        {/* ── Executive Summary ── */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('report.executive_summary')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <PerformancePlot
            performance={normaColorToLevel(ppt.perceptionPerf)}
            potential={normaColorToLevel(ppt.probabilityPerf)}
            segmentLabels={[
              t('report.perf_emerging'),
              t('report.perf_developing'),
              t('report.perf_excelling'),
            ]}
            accentColor={primary}
            width={200}
            height={360}
          />
        </Box>

        {/* ── Legend ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width={20} height={20}>
              <circle
                cx={10}
                cy={10}
                r={10}
                fill={primary}
                fillOpacity={0.15}
                stroke={primary}
                strokeWidth={2}
              />
              <circle cx={10} cy={10} r={4.5} fill={primary} />
            </svg>
            <Typography variant="caption" color="text.secondary">
              {t('report.legend_perf_dot')}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: primary,
                opacity: 0.15,
                border: `1.5px solid ${primary}`,
                borderRadius: '3px',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('report.legend_reach_area')}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <svg width={18} height={18}>
              <defs>
                <linearGradient id="legend-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={primary} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={primary} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <rect
                x={0}
                y={0}
                width={18}
                height={18}
                fill="url(#legend-gradient)"
                rx={3}
                stroke={primary}
                strokeWidth={1.5}
                strokeOpacity={0.4}
              />
            </svg>
            <Typography variant="caption" color="text.secondary">
              {t('report.legend_gradient')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          {ppt.warnings.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <Typography variant="body1" color="text.secondary" fontWeight={700}>
                {t('report.warnings_label')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {ppt.warnings
                  .map((w) => (WARNING_KEY_MAP[w] ? t(WARNING_KEY_MAP[w]) : w))
                  .join(', ')}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, maxWidth: 720, textAlign: 'justify' }}
        >
          {t('report.executive_summary_desc')}
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* ── Le Norme del Team ── */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {t('report.norms_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'justify' }}>
          {t('report.norms_desc')}
        </Typography>
        <NormsHeatmap normsColors={ppt.normsColors} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, textAlign: 'justify' }}>
          {t('report.norms_caption')}
        </Typography>

        <Divider sx={{ my: 4 }} />

        {/* ── Classificazione Aree ── */}
        {areaClassifications && (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t('report.area_classifications_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'justify' }}>
              {t('report.recommendations_desc')}
            </Typography>
            <AreaClassificationsSection areaClassifications={areaClassifications} />
            <Divider sx={{ my: 4 }} />
          </>
        )}

        {/* ── Dimensioni Relazionali ── */}
        {relational && (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t('analysis.relational_dims')}
            </Typography>
            <RelationalDimsSection relational={relational} />
            <Divider sx={{ my: 4 }} />
          </>
        )}

        {/* ── Raccomandazioni ── */}
        {performanceRecommendations && (
          <>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t('report.recommendations_title')}
            </Typography>
            <RecommendationsSection recommendations={performanceRecommendations} />
            <Divider sx={{ my: 4 }} />
          </>
        )}

        {/* ── Temi Emergenti ── */}
        {themes.length > 0 && (
          <Box className="temi-section">
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t('report.themes_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {themes.map((theme, i) => {
                const newlineIndex = theme.indexOf('\n');
                const title = newlineIndex > 0 ? theme.slice(0, newlineIndex).trim() : '';
                const body = newlineIndex > 0 ? theme.slice(newlineIndex + 1).trim() : theme;
                return (
                  <Card key={i} variant="outlined">
                    <CardContent>
                      {title && (
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          {title}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {body}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
